import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { listBranches } from '../../data/fetchers';
import { ChangePasswordModal } from '../../features/auth/ChangePasswordModal';
import { dashboardApi } from '../../lib/apiClient';
import {
  canAccessDashboardRoute,
  canEditSystemConfig,
  canSeeAllBranches,
  visibleBranchIds,
} from '../../lib/permissions';
import { clearAuthenticatedQueryCache } from '../../lib/queryClient';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';

const ROLE_TH: Record<string, string> = {
  owner: 'เจ้าของ',
  manager: 'ผู้จัดการ',
  staff: 'พนักงาน',
  cashier: 'แคชเชียร์',
};

const BRANCH_SCOPED_PATHS = new Set<string>([
  '/',
  '/sales/wholesale',
  '/reports/daily',
  '/reports/monthly',
  '/reports/by-branch',
  '/reports/product-sales',
  '/reports/rounds',
  '/reports/vat',
  '/reports/inventory',
  '/tax-invoices',
  '/stock',
  '/goods-receive',
  '/movements',
  '/transfers',
  '/audit',
  '/operations/backups',
]);

const OverviewIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="7" height="9" />
    <rect x="14" y="3" width="7" height="5" />
    <rect x="14" y="12" width="7" height="9" />
    <rect x="3" y="16" width="7" height="5" />
  </svg>
);

const ChartIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ProductIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const StockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const SettingsIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const CpuIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
    <rect x="9" y="9" width="6" height="6" />
    <line x1="9" y1="1" x2="9" y2="4" />
    <line x1="15" y1="1" x2="15" y2="4" />
    <line x1="9" y1="20" x2="9" y2="23" />
    <line x1="15" y1="20" x2="15" y2="23" />
    <line x1="20" y1="9" x2="23" y2="9" />
    <line x1="20" y1="15" x2="23" y2="15" />
    <line x1="1" y1="9" x2="4" y2="9" />
    <line x1="1" y1="15" x2="4" y2="15" />
  </svg>
);

const GROUPS: {
  label?: string;
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  color?: string;
  items: { to: string; th: string; ownerOnly?: boolean }[];
}[] = [
  { items: [{ to: '/', th: 'ภาพรวม' }] },
  {
    label: 'การขาย',
    icon: ProductIcon,
    color: 'text-rose-400',
    items: [{ to: '/sales/wholesale', th: 'ขายส่ง / ออกบิล' }],
  },
  {
    label: 'รายงาน',
    icon: ChartIcon,
    color: 'text-sky-400',
    items: [
      { to: '/reports/daily', th: 'ยอดขายรายวัน' },
      { to: '/reports/monthly', th: 'ยอดขายรายเดือน' },
      { to: '/reports/by-branch', th: 'ยอดขายแยกสาขา' },
      { to: '/reports/product-sales', th: 'ยอดขายและกำไรรายสินค้า' },
      { to: '/reports/rounds', th: 'รอบขาย & เงินสด' },
      { to: '/reports/vat', th: 'สรุป VAT' },
      { to: '/reports/inventory', th: 'รายงานสต๊อก' },
      { to: '/reports/z-report', th: 'Z-Report' },
      { to: '/tax-invoices', th: 'ใบกำกับภาษี' },
      { to: '/reports/receipts', th: 'ประวัติใบเสร็จ' },
    ],
  },
  {
    label: 'สินค้า',
    icon: ProductIcon,
    color: 'text-emerald-400',
    items: [
      { to: '/products', th: 'รายการสินค้า' },
      { to: '/categories', th: 'หมวดหมู่' },
      { to: '/pricing', th: 'ราคาแยกสาขา' },
    ],
  },
  {
    label: 'สต๊อก',
    icon: StockIcon,
    color: 'text-amber-400',
    items: [
      { to: '/stock', th: 'สต๊อกแยกสาขา' },
      { to: '/movements', th: 'ประวัติการเคลื่อนไหว' },
      { to: '/transfers', th: 'โอนย้ายระหว่างสาขา' },
      { to: '/goods-receive', th: 'รับเข้าสินค้า' },
    ],
  },
  {
    label: 'ตั้งค่า',
    icon: SettingsIcon,
    color: 'text-indigo-400',
    items: [
      { to: '/users', th: 'ผู้ใช้และสิทธิ์', ownerOnly: true },
      { to: '/branches', th: 'ร้านค้าและสาขา', ownerOnly: true },
      { to: '/payment-methods', th: 'วิธีชำระเงิน' },
      { to: '/promotions', th: 'โปรโมชั่น' },
      { to: '/audit', th: 'ประวัติการใช้งาน' },
      { to: '/settings', th: 'ตั้งค่าระบบ', ownerOnly: true },
    ],
  },
  {
    label: 'ระบบ/ปฏิบัติการ',
    icon: CpuIcon,
    color: 'text-pink-400',
    items: [
      { to: '/operations/sync-health', th: 'สถานะซิงก์' },
      { to: '/payments/reconciliation', th: 'กระทบยอดชำระ' },
      { to: '/payments/evidence', th: 'ตรวจหลักฐานโอน' },
      { to: '/devices/health', th: 'เครื่อง POS' },
      { to: '/operations/price-checkers', th: 'จุดเช็กราคา' },
      { to: '/operations/backups', th: 'สำรองข้อมูล' },
    ],
  },
];

export function Sidebar() {
  const { mobileNavOpen, setMobileNav, openGroups, toggleGroup } = useUi();
  const user = useSession((s) => s.user);
  const isOwner = user ? canEditSystemConfig(user) : false;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useSession((s) => s.logout);
  const branchId = useUi((s) => s.branchId);
  const setBranch = useUi((s) => s.setBranch);

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const allowedIds = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];
  const visibleBranches = branches.filter((b) => allowedIds.includes(b.id));
  const canAll = user ? canSeeAllBranches(user) : false;
  const showBranchPicker = BRANCH_SCOPED_PATHS.has(pathname);

  const onLogout = async () => {
    await dashboardApi.logout().catch(() => undefined);
    clearAuthenticatedQueryCache();
    logout();
    setBranch(null);
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name.trim().charAt(0).toUpperCase();
  };

  return (
    <>
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden backdrop-blur-xs"
          onClick={() => setMobileNav(false)}
          aria-hidden="true"
        />
      )}

      <nav
        className={`fixed lg:static z-40 w-[252px] shrink-0 bg-ink text-white/90 h-dvh overflow-hidden py-5 flex flex-col justify-between transition-transform lg:translate-x-0 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Brand Header */}
          <div className="px-6 pb-5 border-b border-white/5 mb-6 select-none">
            <div className="flex items-center gap-2.5">
              <img
                src="/pangsuay_logo.png"
                alt="โลโก้แป้งสวย POS"
                className="w-9 h-9 rounded-xl bg-white object-contain shadow-lg"
              />
              <div>
                <h2 className="text-body font-bold text-white tracking-wide">
                  ร้านแป้งสวย
                </h2>
                <p className="text-[13px] text-white/70 mt-0.5">
                  ระบบจัดการหลังร้าน
                </p>
              </div>
            </div>
          </div>

          {/* Branch Selector (Desktop & Mobile Drawer) */}
          {user && showBranchPicker && (
            <div className="px-3 mb-6 select-none">
              <div className="relative">
                <label className="block text-[13px] font-medium text-white/70 mb-1.5 px-1">
                  สาขาที่ทำงาน
                </label>
                <select
                  aria-label="เลือกสาขา"
                  className="w-full h-10 pl-3 pr-8 rounded-xl border border-white/[0.08] bg-white/[0.03] text-sm text-white/90 font-semibold transition-all duration-200 hover:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none cursor-pointer"
                  value={branchId ?? ''}
                  onChange={(e) => setBranch(e.target.value || null)}
                >
                  {canAll && (
                    <option value="" className="bg-ink text-white">
                      ทุกสาขา
                    </option>
                  )}
                  {visibleBranches.map((b) => (
                    <option
                      key={b.id}
                      value={b.id}
                      className="bg-ink text-white"
                    >
                      {b.short}
                    </option>
                  ))}
                </select>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-white/40 mt-5">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="space-y-1">
            {GROUPS.map((g, i) => {
              const items = g.items.filter((it) => !it.ownerOnly || isOwner);
              if (items.length === 0) return null;
              const visibleItems = items.filter((it) =>
                user ? canAccessDashboardRoute(user, it.to) : false,
              );
              if (visibleItems.length === 0) return null;

              if (!g.label) {
                // Overview special card layout
                return (
                  <div key={i} className="px-3 mb-4">
                    <div>
                      {visibleItems.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end
                          onClick={() => setMobileNav(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3.5 py-2.5 text-[14px] rounded-btn transition-all duration-150 ${
                              isActive
                                ? 'bg-surface text-ink font-semibold after:ml-auto after:h-1.5 after:w-1.5 after:rounded-full after:bg-primary'
                                : 'text-white/70 hover:text-white hover:bg-white/[0.03] font-medium'
                            }`
                          }
                        >
                          <OverviewIcon className="w-4 h-4 opacity-80" />
                          <span>{it.th}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              const hasActive = visibleItems.some(
                (it) => pathname === it.to || pathname.startsWith(`${it.to}/`),
              );
              const open = openGroups[g.label] ?? hasActive;

              return (
                <div key={i} className="px-3 mb-1">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleGroup(g.label!, open)}
                    className={`w-full flex items-center justify-between gap-2.5 px-3.5 min-h-11 rounded-btn text-[12px] font-semibold tracking-wide transition-colors select-none hover:bg-white/[0.03] hover:text-white ${
                      hasActive ? 'text-white' : 'text-white/55'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      {g.icon && <g.icon className="w-4 h-4 opacity-80" />}
                      <span>{g.label}</span>
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className={`w-3.5 h-3.5 opacity-50 transition-transform ${open ? '' : '-rotate-90'}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {/* Kept mounted (hidden) so collapse is instant and cheap. */}
                  <div hidden={!open} className="mb-3 space-y-0.5">
                    {visibleItems.map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.to === '/'}
                        onClick={() => setMobileNav(false)}
                        className={({ isActive }) =>
                          // pl-10 lines the text up with the group label (px-3.5 + icon + gap-2.5).
                          `flex items-center min-h-11 pl-10 pr-3.5 py-2 text-[14px] rounded-btn transition-colors duration-150 ${
                            isActive
                              ? 'bg-surface text-ink font-semibold after:ml-auto after:h-1.5 after:w-1.5 after:rounded-full after:bg-primary'
                              : 'text-white/60 hover:text-white hover:bg-white/[0.03] font-medium'
                          }`
                        }
                      >
                        {it.th}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar User Profile & Footer info */}
        {user && (
          <div className="shrink-0 border-t border-white/5 pt-4 px-3 mt-4 relative">
            {profileMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setProfileMenuOpen(false)}
                />
                <div className="absolute bottom-16 left-3 right-3 z-50 bg-[#242320] border border-white/[0.08] rounded-xl p-1.5 shadow-xl flex flex-col gap-0.5 bo-fade">
                  <button
                    onClick={() => {
                      setChangePasswordOpen(true);
                      setProfileMenuOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2.5 text-[13px] text-white/80 hover:text-white hover:bg-white/[0.03] rounded-btn flex items-center gap-2.5 font-semibold transition-colors"
                  >
                    เปลี่ยนรหัสผ่าน
                  </button>

                  <hr className="border-white/5 my-1" />

                  <button
                    onClick={() => {
                      void onLogout();
                      setProfileMenuOpen(false);
                    }}
                    className="w-full text-left px-3.5 py-2.5 text-[13px] text-danger hover:bg-[#C8362A]/10 hover:text-[#C8362A] rounded-btn flex items-center gap-2.5 font-bold transition-colors"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    ออกจากระบบ
                  </button>
                </div>
              </>
            )}

            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="w-full flex items-center justify-between p-2 rounded-xl hover:bg-white/[0.03] transition-colors group select-none"
              aria-label="เมนูผู้ใช้งาน"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/15 text-white font-medium flex items-center justify-center border border-primary/20 text-xs">
                  {getInitials(user.name)}
                </div>
                <div className="text-left leading-tight">
                  <div className="text-[13px] font-bold text-white/90 group-hover:text-white">
                    {user.name}
                  </div>
                  <div className="text-[13px] text-white/70">
                    {ROLE_TH[user.role] || user.role}
                  </div>
                </div>
              </div>
              <span className="text-white/30 text-[10px] pr-1">▴</span>
            </button>

            <div className="px-3 pt-3 text-[10px] text-white/30 font-mono select-none">
              PangSuay v0.1.0
            </div>
          </div>
        )}
      </nav>
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
}
